// src/pages/motolivraison/motolivraison-dashboard.page.jsx
//
// Tableau de bord MotoLivraison — vue de pilotage.
//
// Organisation, du plus décisionnel au plus détaillé :
//   1. Période + rafraîchissement
//   2. « À traiter » : ce qui bloque et attend une décision admin (cliquable)
//   3. 4 indicateurs clés avec tendance vs période précédente + sparkline
//   4. Évolution commandes / revenus (graphe principal)
//   5. Répartition par statut et par mode de commande
//   6. État du réseau (commerces, livreurs, catalogue)
//   7. Top commerces / Top livreurs
//   8. Dernières commandes
//
// Entièrement responsive : 4 colonnes en desktop, 2 en tablette, 1 en mobile.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, message, Breadcrumb, Spin, Tag, Space, Button, Segmented,
  Empty, Progress, Avatar, Table, Typography, Tooltip as AntTooltip,
} from 'antd';
import {
  ShopOutlined, CarOutlined, ContainerOutlined, DollarOutlined,
  RightOutlined, ClockCircleOutlined, CheckCircleOutlined, PercentageOutlined,
  AppstoreOutlined, ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined,
  MobileOutlined, PhoneOutlined, UserOutlined, WarningOutlined, RiseOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, Area, AreaChart, PieChart, Pie, Cell,
} from 'recharts';
import { motolivraisonService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import { LOCALE_TABLE, cmpTexte } from './table.helpers';
import './css/motolivraison-table.css';

const { Text } = Typography;

const PERIODES = [
  { value: 'daily', label: "Aujourd'hui" },
  { value: 'weekly', label: '7 jours' },
  { value: 'monthly', label: '30 jours' },
  { value: 'all', label: 'Tout' },
];

// Palette : doré TIGOON pour l'argent, ardoise pour les volumes, vert/rouge pour les états
const C = {
  or: '#fa8c16',
  ardoise: '#334155',
  bleu: '#1677ff',
  vert: '#52c41a',
  rouge: '#f5222d',
  violet: '#722ed1',
  gris: '#8c8c8c',
};

const STATUTS = {
  en_attente_paiement: { label: 'Attente paiement', color: '#faad14' },
  payee: { label: 'Payée', color: '#13c2c2' },
  en_attente_livreur: { label: 'Attente livreur', color: '#fa8c16' },
  assignee: { label: 'Assignée', color: '#1677ff' },
  recuperee: { label: 'Récupérée', color: '#722ed1' },
  en_livraison: { label: 'En livraison', color: '#2f54eb' },
  livree: { label: 'Livrée', color: '#a0d911' },
  confirmee: { label: 'Confirmée', color: '#52c41a' },
  soldee: { label: 'Soldée', color: '#389e0d' },
  annulee: { label: 'Annulée', color: '#f5222d' },
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtF = (n) => `${fmt(Math.round(Number(n || 0)))} F`;
// Format court pour les axes : 1 250 000 → 1,3 M
const fmtCourt = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.', ',')} M`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1000)} k`;
  return String(v);
};

// ─── Carte d'indicateur clé : valeur, évolution et mini-courbe ──────────────
const KpiCard = ({ titre, valeur, sousTitre, icone, couleur, serie, cle, variation }) => (
  <Card size="small" styles={{ body: { padding: 16 } }} style={{ height: '100%' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ minWidth: 0 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{titre}</Text>
        <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25, color: couleur, wordBreak: 'break-word' }}>
          {valeur}
        </div>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>{sousTitre}</div>
      </div>
      <span
        style={{
          background: `${couleur}1a`, color: couleur, borderRadius: 10,
          width: 38, height: 38, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 18, flexShrink: 0,
        }}
      >
        {icone}
      </span>
    </div>

    {variation !== null && variation !== undefined && (
      <div style={{ marginTop: 8, fontSize: 12 }}>
        <span style={{ color: variation >= 0 ? C.vert : C.rouge, fontWeight: 600 }}>
          {variation >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(variation).toFixed(0)} %
        </span>
        <span style={{ color: '#bfbfbf' }}> vs période précédente</span>
      </div>
    )}

    {serie?.length > 1 && (
      <div style={{ height: 42, marginTop: 8, marginLeft: -4, marginRight: -4 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={serie} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`grad-${cle}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={couleur} stopOpacity={0.35} />
                <stop offset="100%" stopColor={couleur} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey={cle} stroke={couleur} strokeWidth={2} fill={`url(#grad-${cle})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </Card>
);

// ─── Pastille « à traiter » cliquable ───────────────────────────────────────
const PastilleAction = ({ nombre, libelle, couleur, onClick }) => (
  <Button
    onClick={onClick}
    style={{
      height: 'auto', padding: '8px 14px', borderRadius: 10, textAlign: 'left',
      borderColor: `${couleur}55`, background: `${couleur}0f`,
    }}
  >
    <Space size={10}>
      <span style={{ fontSize: 20, fontWeight: 700, color: couleur }}>{nombre}</span>
      <span style={{ fontSize: 13, color: '#434343' }}>{libelle}</span>
    </Space>
  </Button>
);

const MotolivraisonDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('monthly');
  const [majLe, setMajLe] = useState(null);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getStats(periode);
      setStats(data);
      setMajLe(dayjs());
    } catch (e) {
      message.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [periode]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ─── Données dérivées ────────────────────────────────────────────────────
  const serie = useMemo(() => (stats?.serie || []).map(j => ({
    ...j,
    jour: dayjs(j.date).format('DD/MM'),
  })), [stats]);

  // Évolution : on compare la 2e moitié de la période à la 1re.
  // Sur un seul jour de données la comparaison n'a pas de sens → null.
  const variations = useMemo(() => {
    if (serie.length < 4) return {};
    const milieu = Math.floor(serie.length / 2);
    const somme = (arr, cle) => arr.reduce((t, j) => t + Number(j[cle] || 0), 0);
    const calc = (cle) => {
      const avant = somme(serie.slice(0, milieu), cle);
      const apres = somme(serie.slice(milieu), cle);
      if (!avant) return apres ? 100 : null;
      return ((apres - avant) / avant) * 100;
    };
    return { commandes: calc('commandes'), revenusTTC: calc('revenusTTC'), commission: calc('commission') };
  }, [serie]);

  const parStatut = useMemo(() => {
    const src = stats?.commandes?.parStatut || {};
    return Object.entries(src)
      .map(([statut, v]) => ({
        statut,
        label: STATUTS[statut]?.label || statut,
        color: STATUTS[statut]?.color || C.gris,
        value: v.count,
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [stats]);

  const parMode = useMemo(() => {
    const src = stats?.commandes?.parMode || {};
    return [
      { mode: 'Application', count: src.app?.count || 0, montant: src.app?.montantTTC || 0, color: C.bleu },
      { mode: 'Téléphone', count: src.telephone?.count || 0, montant: src.telephone?.montantTTC || 0, color: C.violet },
    ];
  }, [stats]);

  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!stats) return <Empty description="Aucune donnée" style={{ padding: 60 }} />;

  const { commerces, livreurs, commandes, revenus, reversements, catalogue, aTraiter } = stats;
  const totalCommandes = commandes?.total || 0;
  const livrees = ['livree', 'confirmee', 'soldee']
    .reduce((t, k) => t + (commandes?.parStatut?.[k]?.count || 0), 0);
  const annulees = commandes?.parStatut?.annulee?.count || 0;
  const panierMoyen = totalCommandes ? (revenus?.montantTTCTotal || 0) / totalCommandes : 0;
  const tauxCommission = revenus?.montantTTCTotal
    ? ((revenus.commissionPlateforme / revenus.montantTTCTotal) * 100)
    : 0;
  const tauxLivraison = totalCommandes ? (livrees / totalCommandes) * 100 : 0;

  const actions = [
    { n: aTraiter?.commercantsPending || 0, l: 'commerçants à valider', c: C.or, to: '/dashboard/motolivraison/commercants' },
    { n: aTraiter?.commercesPending || 0, l: 'boutiques à valider', c: C.or, to: '/dashboard/motolivraison/commerces' },
    { n: aTraiter?.livreursPending || 0, l: 'livreurs à valider', c: C.bleu, to: '/dashboard/motolivraison/livreurs' },
    { n: aTraiter?.commandesSansLivreur || 0, l: 'commandes sans livreur', c: C.rouge, to: '/dashboard/motolivraison/commandes' },
    { n: aTraiter?.reversementsPending || 0, l: 'reversements à payer', c: C.violet, to: '/dashboard/motolivraison/reversements' },
  ].filter(a => a.n > 0);

  const libellePeriode = PERIODES.find(p => p.value === periode)?.label;

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px 32px' }}>
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 12 }} />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Tableau de bord</Breadcrumb.Item>
        </Breadcrumb>

        {/* ─── 1. Période ─────────────────────────────────────────────── */}
        <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: 12 } }}>
          <Row gutter={[12, 12]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
              <Space wrap size={12}>
                <Text strong style={{ fontSize: 13 }}>Période</Text>
                <Segmented
                  value={periode}
                  onChange={setPeriode}
                  options={PERIODES}
                />
              </Space>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Space size={10} wrap>
                {majLe && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Mis à jour à {majLe.format('HH:mm')}
                  </Text>
                )}
                <Button icon={<ReloadOutlined />} onClick={fetchStats} loading={loading} size="small">
                  Actualiser
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* ─── 2. À traiter ───────────────────────────────────────────── */}
        {actions.length > 0 && (
          <Card
            size="small"
            style={{ marginBottom: 16, borderLeft: `3px solid ${C.or}` }}
            styles={{ body: { padding: 12 } }}
          >
            <Space wrap size={10} align="center">
              <Space size={8}>
                <WarningOutlined style={{ color: C.or }} />
                <Text strong style={{ fontSize: 13 }}>À traiter</Text>
              </Space>
              {actions.map(a => (
                <PastilleAction key={a.l} nombre={a.n} libelle={a.l} couleur={a.c} onClick={() => navigate(a.to)} />
              ))}
            </Space>
          </Card>
        )}

        {/* ─── 3. Indicateurs clés ────────────────────────────────────── */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}>
            <KpiCard
              titre={`Revenus TTC · ${libellePeriode}`}
              valeur={fmtF(revenus?.montantTTCTotal)}
              sousTitre={`dont ${fmtF(revenus?.montantArticles)} reversables aux commerçants`}
              icone={<DollarOutlined />} couleur={C.or}
              serie={serie} cle="revenusTTC" variation={variations.revenusTTC}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <KpiCard
              titre="Commission plateforme"
              valeur={fmtF(revenus?.commissionPlateforme)}
              sousTitre={`${tauxCommission.toFixed(1)} % du chiffre d'affaires`}
              icone={<PercentageOutlined />} couleur={C.vert}
              serie={serie} cle="commission" variation={variations.commission}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <KpiCard
              titre="Commandes"
              valeur={fmt(totalCommandes)}
              sousTitre={`${fmt(livrees)} livrées · ${fmt(annulees)} annulées`}
              icone={<ContainerOutlined />} couleur={C.bleu}
              serie={serie} cle="commandes" variation={variations.commandes}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <KpiCard
              titre="Panier moyen"
              valeur={fmtF(panierMoyen)}
              sousTitre={`Taux de livraison : ${tauxLivraison.toFixed(0)} %`}
              icone={<RiseOutlined />} couleur={C.violet}
            />
          </Col>
        </Row>

        {/* ─── 4. Évolution ───────────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} xl={16}>
            <Card
              title="Évolution des commandes et des revenus"
              size="small"
              styles={{ body: { padding: '16px 8px 8px 0' } }}
              style={{ height: '100%' }}
            >
              {serie.length ? (
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={serie} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="jour" tick={{ fontSize: 11 }} tickMargin={8} minTickGap={18} />
                      <YAxis yAxisId="gauche" tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                      <YAxis yAxisId="droite" orientation="right" tick={{ fontSize: 11 }} tickFormatter={fmtCourt} width={54} />
                      <RTooltip
                        formatter={(value, name) => (
                          name === 'Revenus TTC' ? [fmtF(value), name] : [fmt(value), name]
                        )}
                        labelFormatter={(l) => `Le ${l}`}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="gauche" dataKey="commandes" name="Commandes" fill={C.bleu} radius={[4, 4, 0, 0]} barSize={18} />
                      <Bar yAxisId="gauche" dataKey="annulees" name="Annulées" fill={C.rouge} radius={[4, 4, 0, 0]} barSize={18} />
                      <Line yAxisId="droite" type="monotone" dataKey="revenusTTC" name="Revenus TTC" stroke={C.or} strokeWidth={2.5} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : <Empty description="Aucune commande sur la période" />}
            </Card>
          </Col>

          {/* ─── 5. Répartitions ──────────────────────────────────────── */}
          <Col xs={24} xl={8}>
            <Card title="Commandes par statut" size="small" style={{ marginBottom: 16 }}>
              {parStatut.length ? (
                <Row align="middle" gutter={8}>
                  <Col xs={12}>
                    <div style={{ width: '100%', height: 170 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={parStatut} dataKey="value" nameKey="label" innerRadius="58%" outerRadius="85%" paddingAngle={2}>
                            {parStatut.map(d => <Cell key={d.statut} fill={d.color} />)}
                          </Pie>
                          <RTooltip formatter={(v, n) => [`${fmt(v)} commande${v > 1 ? 's' : ''}`, n]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      {parStatut.map(d => (
                        <div key={d.statut} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                          <span style={{ flex: 1, color: '#595959' }}>{d.label}</span>
                          <strong>{d.value}</strong>
                        </div>
                      ))}
                    </Space>
                  </Col>
                </Row>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune commande" />}
            </Card>

            <Card title="Canal de commande" size="small">
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                {parMode.map(m => {
                  const total = parMode.reduce((t, x) => t + x.count, 0) || 1;
                  const pct = (m.count / total) * 100;
                  return (
                    <div key={m.mode}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <Space size={6}>
                          {m.mode === 'Application' ? <MobileOutlined /> : <PhoneOutlined />}
                          <span>{m.mode}</span>
                        </Space>
                        <span><strong>{fmt(m.count)}</strong> <Text type="secondary">· {fmtF(m.montant)}</Text></span>
                      </div>
                      <Progress percent={Math.round(pct)} strokeColor={m.color} size="small" />
                    </div>
                  );
                })}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* ─── 6. État du réseau ──────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={<Space><ShopOutlined style={{ color: C.or }} />Commerces</Space>}
              extra={<Link to="/dashboard/motolivraison/commerces">Gérer</Link>}
              style={{ height: '100%' }}
            >
              <Row gutter={[8, 12]}>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Total</Text><div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(commerces?.total)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Approuvés</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.vert }}>{fmt(commerces?.approved)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>En attente</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.or }}>{fmt(commerces?.pending)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Suspendus</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.rouge }}>{fmt(commerces?.suspended)}</div></Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card
              size="small"
              title={<Space><CarOutlined style={{ color: C.bleu }} />Livreurs</Space>}
              extra={<Link to="/dashboard/motolivraison/livreurs">Gérer</Link>}
              style={{ height: '100%' }}
            >
              <Row gutter={[8, 12]}>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Total</Text><div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(livreurs?.total)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Approuvés</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.vert }}>{fmt(livreurs?.approved)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>En attente</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.or }}>{fmt(livreurs?.pending)}</div></Col>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>En ligne</Text>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.bleu }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: C.vert, marginRight: 6, verticalAlign: 'middle' }} />
                    {fmt(livreurs?.actifsMaintenant)}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card
              size="small"
              title={<Space><AppstoreOutlined style={{ color: C.violet }} />Catalogue & reversements</Space>}
              extra={<Link to="/dashboard/motolivraison/reversements">Gérer</Link>}
              style={{ height: '100%' }}
            >
              <Row gutter={[8, 12]}>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Catégories actives</Text><div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(catalogue?.categoriesActives)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Articles actifs</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.vert }}>{fmt(catalogue?.articlesActifs)} <Text type="secondary" style={{ fontSize: 12 }}>/ {fmt(catalogue?.articlesTotal)}</Text></div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Reversements à payer</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.or }}>{fmt(reversements?.pendingCount)}</div></Col>
                <Col xs={12}><Text type="secondary" style={{ fontSize: 12 }}>Déjà reversé</Text><div style={{ fontSize: 20, fontWeight: 700, color: C.vert }}>{fmtF(reversements?.montantNetReverse)}</div></Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* ─── 7. Classements ─────────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card size="small" title="Top boutiques (chiffre d'affaires)" style={{ height: '100%' }}>
              {stats.topCommerces?.length ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {stats.topCommerces.map((c, i) => {
                    const max = stats.topCommerces[0].montantTTC || 1;
                    return (
                      <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar size={34} src={c.logo} icon={<ShopOutlined />} style={{ backgroundColor: C.or, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                            <AntTooltip title={c.nom}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {i + 1}. {c.nom || 'Boutique supprimée'}
                              </span>
                            </AntTooltip>
                            <strong style={{ whiteSpace: 'nowrap' }}>{fmtF(c.montantTTC)}</strong>
                          </div>
                          <Progress
                            percent={Math.round((c.montantTTC / max) * 100)}
                            showInfo={false} size="small" strokeColor={C.or}
                          />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {fmt(c.commandes)} commande{c.commandes > 1 ? 's' : ''} · commission {fmtF(c.commission)}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </Space>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune vente sur la période" />}
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card size="small" title="Top livreurs (livraisons terminées)" style={{ height: '100%' }}>
              {stats.topLivreurs?.length ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {stats.topLivreurs.map((l, i) => {
                    const max = stats.topLivreurs[0].livraisons || 1;
                    const nom = `${l.firstName || ''} ${l.lastName || ''}`.trim() || 'Livreur supprimé';
                    return (
                      <div key={l._id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar size={34} icon={<UserOutlined />} style={{ backgroundColor: C.bleu, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {i + 1}. {nom}
                            </span>
                            <strong style={{ whiteSpace: 'nowrap' }}>{fmt(l.livraisons)} livraison{l.livraisons > 1 ? 's' : ''}</strong>
                          </div>
                          <Progress
                            percent={Math.round((l.livraisons / max) * 100)}
                            showInfo={false} size="small" strokeColor={C.bleu}
                          />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {formatPhoneCI(l.phone)} · {fmtF(l.montantTTC)} encaissés
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </Space>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune livraison sur la période" />}
            </Card>
          </Col>
        </Row>

        {/* ─── 8. Dernières commandes ─────────────────────────────────── */}
        <Card
          size="small"
          title="Dernières commandes"
          extra={<Link to="/dashboard/motolivraison/commandes">Voir toutes les commandes</Link>}
          style={{ marginTop: 16 }}
        >
          <Table
            className="moto-table"
            rowKey="_id"
            size="middle"
            dataSource={stats.dernieresCommandes || []}
            locale={{ ...LOCALE_TABLE, emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune commande" /> }}
            pagination={false}
            scroll={{ x: 900 }}
            onRow={(r) => ({
              onClick: () => navigate(`/dashboard/motolivraison/commandes/${r._id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'Réf', key: 'ref', width: 110, render: (_, r) => <Text code>#{String(r._id).slice(-6).toUpperCase()}</Text> },
              {
                title: 'Boutique', key: 'com', width: 200, ellipsis: true,
                sorter: (a, b) => cmpTexte(a.commerceId?.nom, b.commerceId?.nom),
                render: (_, r) => r.commerceId?.nom || '—',
              },
              {
                title: 'Client', key: 'cli', width: 180, ellipsis: true,
                render: (_, r) => r.clientNom || '—',
              },
              {
                title: 'Livreur', key: 'liv', width: 170, ellipsis: true,
                render: (_, r) => r.livreurId
                  ? `${r.livreurId.firstName || ''} ${r.livreurId.lastName || ''}`.trim()
                  : <Text type="secondary">Non assigné</Text>,
              },
              {
                title: 'Mode', dataIndex: 'mode', key: 'mode', width: 100, align: 'center',
                render: (m) => <Tag color={m === 'app' ? 'blue' : 'purple'}>{m === 'app' ? 'App' : 'Tél'}</Tag>,
              },
              {
                title: 'Montant', dataIndex: 'montantTTC', key: 'mt', width: 120, align: 'right',
                render: (v) => <strong>{fmtF(v)}</strong>,
              },
              {
                title: 'Statut', dataIndex: 'statut', key: 'st', width: 150,
                render: (s) => <Tag color={STATUTS[s]?.color ? undefined : 'default'} style={STATUTS[s] ? { color: STATUTS[s].color, borderColor: `${STATUTS[s].color}55`, background: `${STATUTS[s].color}12` } : undefined}>
                  {STATUTS[s]?.label || s}
                </Tag>,
              },
              {
                title: 'Date', dataIndex: 'createdAt', key: 'dt', width: 150,
                render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—',
              },
            ]}
          />
        </Card>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Space size={6} wrap>
            <ClockCircleOutlined style={{ color: '#bfbfbf' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Période analysée : {stats.dateDebut ? dayjs(stats.dateDebut).format('DD/MM/YYYY') : 'depuis le début'}
              {' → '}{dayjs(stats.dateFin).format('DD/MM/YYYY')}
            </Text>
            <CheckCircleOutlined style={{ color: C.vert }} />
          </Space>
        </div>
      </div>
    </div>
  );
};

export default MotolivraisonDashboard;
